import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, resolveAvatar } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Shield, LogOut, User as UserIcon, Pencil, MessageSquare, LogIn } from "lucide-react";
import { SiteNav, MobileNavMenu, AiSearchButton } from "@/components/SiteNav";
import { AISearchDialog } from "@/components/AISearchDialog";
import { NotificationsBell } from "@/components/NotificationsBell";

export const Navbar = () => {  // header = navigate the site; Submit/Upload lives in the Dashboard
  const { user, login, logout, openPersona, openAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const avatarSrc = resolveAvatar(user?.avatar_url) || user?.picture;
  const [unread, setUnread] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = () => api.get("/messages/unread").then((r) => alive && setUnread(r.data.count)).catch(() => {});
    load();
    const id = setInterval(load, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [user, location.pathname]);

  return (
    <>
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70">
      <div className="max-w-[1600px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => navigate("/browse")}
          className="flex items-center gap-3 group"
        >
          <img src="/logo-icon.png" alt="" className="h-9 w-auto" />
          <span className="hidden sm:inline font-heading text-xl sm:text-3xl font-semibold tracking-tight text-primary">
            BareFootage
          </span>
        </button>

        <SiteNav user={user} />

        <div className="flex items-center gap-1.5 sm:gap-3">
          <AiSearchButton onClick={() => setAiOpen(true)} />
          <MobileNavMenu user={user} />
          {user && <NotificationsBell />}
          {user && (
            <button data-testid="nav-messages-btn" onClick={() => navigate("/messages")}
              className="relative h-9 w-9 inline-flex items-center justify-center rounded-full border border-border hover:border-primary hover:text-primary transition-colors">
              <MessageSquare className="w-4 h-4" />
              {unread > 0 && <span className="absolute -top-1 -right-1 text-[9px] font-bold bg-primary text-primary-foreground rounded-full min-w-[16px] h-4 flex items-center justify-center px-1">{unread}</span>}
            </button>
          )}
          {user ? (
            <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="outline-none"
                  onPointerDown={(e) => e.preventDefault()}
                  onClick={() => setMenuOpen((o) => !o)}>
                  <Avatar className="w-9 h-9 border border-border hover:border-primary transition-colors">
                    <AvatarImage src={avatarSrc} alt={user.display_name || user.name} className="object-cover object-[72%_26%]" />
                    <AvatarFallback className="bg-secondary text-xs">
                      {(user.display_name || user.name)?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-popover border-border">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="text-sm">{user.display_name || user.name}</span>
                  <span className="text-xs text-muted-foreground font-normal truncate">{user.email}</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/profile")}>
                  <UserIcon className="w-4 h-4 mr-2" /> My Page
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-messages" onClick={() => navigate("/messages")}>
                  <MessageSquare className="w-4 h-4 mr-2" /> Messages
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="nav-edit-persona" onClick={openPersona}>
                  <Pencil className="w-4 h-4 mr-2" /> Edit Persona
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem data-testid="nav-admin-link" onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4 mr-2" /> Admin Studio
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout" onClick={logout} className="text-destructive focus:text-destructive">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-4">
              <button
                data-testid="nav-login-btn"
                onClick={() => openAuth("login")}
                className="hidden sm:inline text-sm font-semibold text-muted-foreground hover:text-primary transition-colors"
              >
                Sign In
              </button>
              <button
                data-testid="nav-join-btn"
                onClick={() => openAuth("register")}
                className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold shadow-lg shadow-primary/20 hover:-translate-y-0.5 transition-transform"
              >
                Join Free
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
    <AISearchDialog open={aiOpen} onClose={() => setAiOpen(false)} />
    </>
  );
};
